import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

import { CopyButton } from "@components";

const StyledCopyButton = styled(CopyButton)(({ theme }) => ({
  // backgroundColor: "transparent",
  color: theme.palette.grey[700],
  transition: theme.transitions.create(["color", "background-color"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.common.black,
  },
}));
const CodeBlockContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  margin: theme.spacing(1, 0),
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  width: "100%",

  "& pre": {
    // Need to use important here to override external styles
    margin: "0 !important",
    borderRadius: theme.spacing(1),
  },
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  display: "flex",
  gap: theme.spacing(0.5),
}));

const LanguageLabel = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: theme.spacing(1),
  top: theme.spacing(1),
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
  fontSize: "0.75rem",
  fontWeight: 600,
  zIndex: 1,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const StyledSyntaxHighlighter = styled(SyntaxHighlighter)(({ theme }) => ({
  // Need to use important here to override external styles
  padding: `${theme.spacing(4.5)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
}));

interface CodeBlockProps {
  code: string;
  language?: string;
}

const CodeBlock = ({ code, language = "text" }: CodeBlockProps) => {
  const normalizedLanguage = language?.toLowerCase() || "text";

  return (
    <CodeBlockContainer>
      <ActionsContainer className="code-actions">
        <StyledCopyButton text={code} tooltipText="Copy code" size="small" />
      </ActionsContainer>

      {language !== "text" && <LanguageLabel>{language}</LanguageLabel>}

      <StyledSyntaxHighlighter
        language={normalizedLanguage}
        style={vscDarkPlus}
        wrapLongLines={true}
        showLineNumbers={code.split("\n").length > 3}
      >
        {code}
      </StyledSyntaxHighlighter>
    </CodeBlockContainer>
  );
};

export default CodeBlock;
